import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';
import { chatApi, issuesApi } from '../services/api';
import { ChatContext } from '../contexts/ChatContext';
import { detectIntent } from '../utils/detectIntent';
import { logIntentDetection } from '../utils/intentAnalytics';
import { confirmIntent, updateIntentVector, addIntentVector } from '../utils/intentFeedback';
import { SparklesIcon, ArrowPathIcon, PaperAirplaneIcon, PhotoIcon, PlusIcon } from '@heroicons/react/24/outline';
import { ChatService } from '../services/ChatService';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import DraftIssueCard from './DraftIssueCard';
import DraftIssueModal from './DraftIssueModal';
import IntentConfirmationDialog from './IntentConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import { getIssueFields, DEFAULT_ISSUE_FIELDS } from '../utils/issueFields';
import BrainIcon from './common/BrainIcon';
import { BRAND } from '../constants/brand';

const STORAGE_KEY = 'chat_session_id';

const ChatMessage = ({ msg, onChoice }) => {
  const isUser = (msg.sender || (msg.role === 'user' ? 'user' : 'ai')) === 'user';
  const navigate = useNavigate();
  const [issueFields, setIssueFields] = useState(DEFAULT_ISSUE_FIELDS);

  // Fetch issue fields when component mounts
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const fields = await getIssueFields();
        setIssueFields(fields);
      } catch (error) {
        console.error('Error fetching issue fields:', error);
        // Use default fields if API call fails
        setIssueFields(DEFAULT_ISSUE_FIELDS);
      }
    };
    fetchFields();
  }, []);

  const renderMarkdown = (text) => {
    if (!text) return null;
    return (
      <ReactMarkdown 
        className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-strong:text-gray-900 prose-code:text-gray-800 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100"
        components={{
          code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={vscDarkPlus}
                language={match[1]}
                PreTag="div"
                className="rounded-lg border border-gray-200 shadow-sm"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          }
        }}
      >
        {text}
      </ReactMarkdown>
    );
  };

  // Helper function to convert confidence to human-readable labels
  const getConfidenceLabel = (confidence) => {
    const confValue = typeof confidence === 'string' ? parseFloat(confidence) : confidence;
    
    if (confValue >= 0.75) {
      return "should map";
    } else if (confValue >= 0.55) {
      return "other possibilities";
    } else {
      return "low confidence";
    }
  };

  // Handle draft_issue_card UI type
  if (msg.data && msg.data.ui_type === 'draft_issue_card') {
    const draftIssue = msg.data.draft_issue || {};
    const explainability = msg.data.explainability || {};
    const showSubmitButton = msg.data.show_submit_button;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={clsx(
          'rounded-xl px-4 py-3 text-sm whitespace-pre-wrap w-full max-w-3xl shadow-sm',
          'self-start bg-white border border-gray-200'
        )}
      >
        <div className="font-medium mb-2 text-gray-800">AI:</div>
        <div className="mb-3">{msg.content || msg.message}</div>
        
        {/* Draft Issue Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <div className="font-semibold text-blue-900 mb-3">📋 Draft Issue Review</div>
          
          {/* Show draft fields */}
          {Object.entries(draftIssue).map(([field, value]) => {
            const fieldExplain = explainability[field] || {};
            const confidence = fieldExplain.confidence || 0;
            const rationale = fieldExplain.rationale;
            
            if (!value || field === 'owner' || field === 'reviewer') return null;
            
            return (
              <div key={field} className="border-b border-blue-100 pb-2 last:border-b-0">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-gray-700 capitalize">{field.replace(/([A-Z])/g, ' $1').trim()}:</div>
                  {confidence > 0 && (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      confidence >= 0.8 ? 'bg-green-100 text-green-700' : 
                      confidence >= 0.5 ? 'bg-yellow-100 text-yellow-700' : 
                      'bg-red-100 text-red-700'
                    }`}>
                      {getConfidenceLabel(confidence)}
                    </span>
                  )}
                </div>
                <div className="text-gray-600 mt-1">{String(value)}</div>
                {rationale && (
                  <div className="text-xs text-gray-500 italic mt-1">💡 {rationale}</div>
                )}
              </div>
            );
          })}
          
          {/* Submit Button */}
          {showSubmitButton && (
            <div className="flex gap-2 mt-4 pt-3 border-t border-blue-200">
              <button
                onClick={() => onChoice && onChoice('submit')}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                ✅ Submit Issue
              </button>
              <button
                onClick={() => onChoice && onChoice('modify')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ✏️ Modify
              </button>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // Handle enhanced response UI type
  if (msg.data && msg.data.ui_type === 'enhanced_response') {
    const graphInsights = msg.data.graph_insights || {};
    const unifiedContext = msg.data.unified_context || {};
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={clsx(
          'rounded-xl px-4 py-3 text-sm w-full max-w-3xl shadow-sm',
          'self-start bg-blue-50 border border-blue-200'
        )}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="font-medium text-blue-800">AI Response</div>
        </div>
        

        
        <div className="text-blue-700 mb-3 prose prose-sm max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              code: ({ node, inline, className, children, ...props }) => {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={tomorrow}
                    language={match[1]}
                    PreTag="div"
                    className="rounded-lg"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props}>
                    {children}
                  </code>
                );
              },
              table: ({ children }) => (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    {children}
                  </table>
                </div>
              ),
              th: ({ children }) => (
                <th className="px-3 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-b border-gray-200">
                  {children}
                </td>
              )
            }}
          >
            {msg.content || msg.message || ''}
          </ReactMarkdown>
        </div>
        
        {/* Graph Context Summary */}
        {unifiedContext.entities_analyzed > 0 && (
          <div className="bg-white rounded-lg p-3 mb-3 border border-blue-200">
            <div className="font-medium text-blue-800 mb-2">📊 Context Analysis</div>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">{unifiedContext.entities_analyzed}</div>
                <div className="text-blue-600">Entities Analyzed</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">{unifiedContext.relationships_found}</div>
                <div className="text-blue-600">Relationships Found</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">{unifiedContext.entity_types?.length || 0}</div>
                <div className="text-blue-600">Entity Types</div>
              </div>
            </div>
            
            {unifiedContext.entity_types && unifiedContext.entity_types.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-blue-600 mb-1">Analyzed Types:</div>
                <div className="flex flex-wrap gap-1">
                  {unifiedContext.entity_types.map((type, idx) => (
                    <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* High Confidence Relationships */}
        {graphInsights.high_confidence_relationships && graphInsights.high_confidence_relationships.length > 0 && (
          <div className="bg-white rounded-lg p-3 border border-blue-200">
            <div className="font-medium text-blue-800 mb-2">🔗 Key Relationships</div>
            <div className="space-y-1">
              {graphInsights.high_confidence_relationships.slice(0, 3).map((rel, idx) => (
                <div key={idx} className="text-xs text-blue-700 flex items-center justify-between">
                  <span>{rel.source} → {rel.target}</span>
                  <span className="px-1 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                    {Math.round(rel.confidence * 100)}%
                  </span>
                </div>
              ))}
              {graphInsights.high_confidence_relationships.length > 3 && (
                <div className="text-xs text-blue-600 italic">
                  +{graphInsights.high_confidence_relationships.length - 3} more relationships
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    );
  }

  // Handle success UI type
  if (msg.data && msg.data.ui_type === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={clsx(
          'rounded-xl px-4 py-3 text-sm whitespace-pre-wrap w-full max-w-3xl shadow-sm',
          'self-start bg-green-50 border border-green-200'
        )}
      >
        <div className="font-medium mb-2 text-green-800">Success!</div>
        <div className="text-green-700">{renderMarkdown(msg.content || msg.message)}</div>
      </motion.div>
    );
  }

  // Handle issue_created UI type
  if (msg.data && msg.data.ui_type === 'issue_created') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={clsx(
          'rounded-xl px-4 py-3 text-sm whitespace-pre-wrap w-full max-w-3xl shadow-sm',
          'self-start bg-green-50 border border-green-200'
        )}
      >
        <div className="font-medium mb-2 text-green-800">✅ Issue Created Successfully!</div>
        <div className="text-green-700 space-y-2">
          <div className="flex items-center gap-2">
            <span className="font-medium">Issue #{msg.data.issue_id}:</span>
            <span>{msg.data.title}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">Rating:</span>
            <span className={clsx(
              'px-2 py-1 rounded text-xs font-medium',
              msg.data.rating === 'CRITICAL' && 'bg-red-100 text-red-800',
              msg.data.rating === 'HIGH' && 'bg-orange-100 text-orange-800',
              msg.data.rating === 'MEDIUM' && 'bg-yellow-100 text-yellow-800',
              msg.data.rating === 'LOW' && 'bg-green-100 text-green-800'
            )}>
              {msg.data.rating}
            </span>
          </div>
          {msg.data.risks_found > 0 && (
            <div className="flex items-center gap-2">
              <span className="font-medium">Risks Identified:</span>
              <span>{msg.data.risks_found} potential mappings</span>
            </div>
          )}
          <div className="text-sm text-green-600 mt-2">
            Enhanced with AI semantic matching and regulatory assessment
          </div>
        </div>
        <div className="mt-3 text-gray-700">
          {msg.content || msg.message}
        </div>
      </motion.div>
    );
  }

  // Render choice buttons if present
  if (msg.data && msg.data.ui_type === 'choice' && Array.isArray(msg.data.choices)) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={clsx(
          'rounded-xl px-4 py-3 text-sm whitespace-pre-wrap max-w-[75%] shadow-sm',
          'bg-gray-100 text-gray-800 self-start'
        )}
      >
        <div className="font-medium mb-1">AI:</div>
        <div>{msg.content || msg.message}</div>
        <div className="flex gap-2 mt-2">
          {msg.data.choices.map((choice) => (
            <button
              key={choice}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={() => onChoice(choice)}
              disabled={msg.sending}
            >
              {choice}
            </button>
          ))}
        </div>
      </motion.div>
    );
  }

  // Render DraftIssueCard if explainability is present
  if (msg.data?.explainability) {
    const explainability = msg.data.explainability;
    // Map explainability keys to canonical field definitions
    const fields = Object.keys(explainability).map((key) => {
      // Try to find a canonical field definition
      const canonical = issueFields.find(f => (f.system_name || f.system) === key);
      if (canonical) {
        return {
          system_name: canonical.system_name || canonical.system,
          display_name: canonical.display_name || canonical.field,
          field_type: canonical.field_type || canonical.type || 'text',
          options: canonical.options || undefined,
        };
      } else {
        // Fallback for unknown fields
        return {
          system_name: key,
          display_name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          field_type: 'text',
        };
      }
    });
    const draftIssue = {
      ...Object.fromEntries(Object.entries(explainability).map(([k, v]) => [k, v.value])),
      confidence: Object.fromEntries(Object.entries(explainability).map(([k, v]) => [k, v.confidence])),
      rationale: Object.fromEntries(Object.entries(explainability).map(([k, v]) => [k, v.rationale]))
    };
    // Remove JSON from message content if explainability is present
    const cleanedMsg = msg.content && msg.content.includes('{') ? '' : msg.content;
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={clsx(
          'rounded-xl px-4 py-3 text-sm whitespace-pre-wrap w-full max-w-3xl shadow-sm',
          'self-start bg-white'
        )}
      >

        <DraftIssueCard
          draftIssue={draftIssue}
          onFieldChange={() => {}}
          fields={fields}
          status="draft"
          onViewDraft={() => handleOpenDraftFromMessage(explainability)}
        />
        {cleanedMsg && (
          <div className="mt-2">{renderMarkdown(cleanedMsg)}</div>
        )}
      </motion.div>
    );
  }

  // Render submitted issue card if system message with isIssueCreated
  if (msg.type === 'system' && msg.isIssueCreated && msg.issueId) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={clsx(
          'rounded-xl px-4 py-3 text-sm whitespace-pre-wrap max-w-[75%] shadow-sm',
          'self-start'
        )}
      >
        <DraftIssueCard
          draftIssue={{ title: msg.content?.match(/\[(.*?)\]/)?.[1] || 'Submitted Issue' }}
          onFieldChange={() => {}}
          fields={[{ system_name: 'title', display_name: 'Title', field_type: 'text' }]}
          status="submitted"
          issueId={msg.issueId}
        />
      </motion.div>
    );
  }

  // Fallback: If no explainability in data, but content looks like JSON, try to extract and render DraftIssueCard
  if (!msg.data?.explainability && msg.content && msg.content.includes('{')) {
    // Try to extract the first JSON object from the content
    const match = msg.content.match(/({[\s\S]*?})\s*(?:\n|$)/);
    if (match) {
      try {
        const explainability = JSON.parse(match[1]);
        
        // Check if the object has the expected structure with value, confidence, and rationale
        const hasValidStructure = Object.values(explainability).every(
          field => field && typeof field === 'object' && 'value' in field
        );
        
        if (hasValidStructure) {
          const fields = Object.keys(explainability).map((key) => ({
            system_name: key,
            display_name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            field_type: 'text',
          }));
          
          const draftIssue = {
            ...Object.fromEntries(Object.entries(explainability).map(([k, v]) => [k, v.value])),
            confidence: Object.fromEntries(Object.entries(explainability).map(([k, v]) => [k, v.confidence])),
            rationale: Object.fromEntries(Object.entries(explainability).map(([k, v]) => [k, v.rationale])),
          };
          
          return (
            <div className="chat-bubble-draft">
              <DraftIssueCard draftIssue={draftIssue} onFieldChange={() => {}} fields={fields} status="draft" />
            </div>
          );
        }
      } catch (e) {
        // Silently handle JSON parsing errors
      }
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={clsx(
        'flex gap-3 mb-6 group',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      {/* AI Avatar */}
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center shadow-md overflow-hidden">
          <BrainIcon className="h-8 w-8" />
        </div>
      )}

      {/* Message Bubble */}
      <div
        className={clsx(
          'relative max-w-[70%] rounded-2xl px-4 py-3 shadow-sm',
          isUser
            ? 'bg-gradient-to-r from-gray-700 via-gray-800 to-gray-900 text-white'
            : 'bg-white/90 backdrop-blur-sm border border-gray-200/60 text-gray-800'
        )}
        style={!isUser ? {
          backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.9) 100%)',
          backdropFilter: 'blur(20px)',
        } : {}}
      >
        {/* Message Content */}
        <div className="text-sm leading-relaxed">
          {msg.type === 'file' ? (
            <a
              href={msg.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-gray-900 underline font-medium"
            >
              📎 {msg.fileName}
            </a>
          ) : msg.isThinking ? (
            <div className="flex items-center gap-2 text-gray-500">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-700 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span className="font-medium">{BRAND.copilot} is thinking...</span>
            </div>
          ) : (
            renderMarkdown(msg.content || msg.message)
          )}
        </div>

        {/* Timestamp */}
        {msg.timestamp && (
          <div className={clsx(
            'text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity',
            isUser ? 'text-white/70' : 'text-gray-500'
          )}>
            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      {/* User Avatar */}
      {isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-gray-500 to-gray-700 flex items-center justify-center shadow-md">
          <span className="text-white font-medium text-sm">U</span>
        </div>
      )}
    </motion.div>
  );
};

const suggestedPrompts = [
  '🔍 Log a new issue',
  '📋 Show me overdue actions',
  '⚠️ Create a new risk assessment',
  '📊 What are my open issues?',
  '🛡️ Suggest controls for GDPR compliance',
  '📈 Generate analytics report',
];

const ChoiceButtons = ({ choices, onSelect }) => (
  <div className="flex gap-3 my-2">
    {choices.map((choice, idx) => (
      <button
        key={choice.value || choice.label || idx}
        className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600"
        onClick={() => onSelect(choice.value || choice.label)}
      >
        {choice.label || choice}
      </button>
    ))}
  </div>
);

const ChatAssistant = ({ children, isCompact = false, isComplianceMode = false, complianceContext = null }) => {
  const [sessionId, setSessionId] = useState(() => {
    const storedId = localStorage.getItem(STORAGE_KEY);
    return storedId || null;
  });
  const [error, setError] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const queryClient = useQueryClient();
  const messagesEndRef = useRef(null);
  const [pendingIntent, setPendingIntent] = useState(null);
  const [confirmationStep, setConfirmationStep] = useState(0);
  const [clarification, setClarification] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [thinkingStatus, setThinkingStatus] = useState(null);
  const chatServiceRef = useRef(null);
  const [draftIssue, setDraftIssue] = useState(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftModalLoading, setDraftModalLoading] = useState(false);
  const [selectOptions, setSelectOptions] = useState({});
  const navigate = useNavigate();
  
  // 🚀 OPTIMIZED INTENT DETECTION STATE
  const [intentConfirmation, setIntentConfirmation] = useState({
    visible: false,
    intentMatch: null,
    originalMessage: '',
    loading: false
  });

  const { data: chatHistory = { messages: [] }, isLoading: historyLoading } = useQuery({
    queryKey: ['chatHistory', sessionId],
    queryFn: () => chatApi.getChatHistory(sessionId),
    refetchOnMount: true,
    retry: false,
    enabled: !!sessionId,
    select: (data) => ({
      ...data,
      messages: (data?.messages || []).map(msg => {
        if (msg.sender) return msg;
        if (msg.role === 'assistant') return { ...msg, sender: 'ai' };
        if (msg.role === 'user') return { ...msg, sender: 'user' };
        return msg;
      }),
    }),
    onError: (error) => {
      if (error.response?.status === 404) {
        // handleNewChat will be defined later
        localStorage.removeItem(STORAGE_KEY);
        window.location.reload();
      } else {
        setError('Failed to load chat history. Please try refreshing the page.');
      }
    },
  });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Create a new session
  const createNewSession = useCallback(async () => {
    try {
      const { session_id } = await chatApi.createSession();
      setSessionId(session_id);
      localStorage.setItem(STORAGE_KEY, session_id);
      setError(null);
      return session_id;
    } catch (error) {
      setError('Failed to create chat session. Please try refreshing the page.');
      return null;
    }
  }, []);

  // Initialize session on mount
  useEffect(() => {
    const initializeSession = async () => {
      const storedId = localStorage.getItem(STORAGE_KEY);
      if (storedId) {
        try {
          await chatApi.getChatHistory(storedId);
          setSessionId(storedId);
          return;
        } catch (error) {
          if (error.response?.status === 404) {
            localStorage.removeItem(STORAGE_KEY);
            await createNewSession();
          } else {
            setError('Failed to load chat history. Please try refreshing the page.');
          }
        }
      } else {
        await createNewSession();
      }
    };
    initializeSession();
  }, [createNewSession]);

  // Handle new chat creation
  const handleNewChat = useCallback(async () => {
    try {
      const newSessionId = await createNewSession();
      if (newSessionId) {
        queryClient.removeQueries(['chatHistory', sessionId]);
      }
    } catch (error) {
      setError('Failed to start new chat. Please try again.');
    }
  }, [createNewSession, queryClient, sessionId]);

  useEffect(() => {
    if (sessionId) {
      chatServiceRef.current = new ChatService(null, sessionId);
    }
  }, [sessionId]);

  // Add user message to UI immediately by updating the RQ cache
  const addMessage = useCallback((message) => {
    queryClient.setQueryData(['chatHistory', sessionId], (old) => ({
      ...old,
      messages: [...(old?.messages || []), message]
    }));
  }, [queryClient, sessionId]);

  // File upload handler
  const handleFileUpload = async (file) => {
    if (!file) return;
    // For demo: just show file as a message (no backend upload)
    const fileUrl = URL.createObjectURL(file);
    const fileMessage = {
      id: `file_${Date.now()}`,
      sender: 'user',
      type: 'file',
      fileName: file.name,
      fileUrl,
      timestamp: new Date().toISOString(),
    };
    addMessage(fileMessage);
    console.log('[Chat] File uploaded:', file.name);
    // TODO: Implement backend upload and AI file handling
  };

  // 🚀 OPTIMIZED INTENT DETECTION FUNCTIONS
  const detectIntentOptimized = async (message) => {
    try {
      const response = await fetch('/api/optimized-intent/detect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error('Intent detection failed');
      }

      const result = await response.json();
      console.log('🚀 Optimized intent detected:', result);
      return result;
    } catch (error) {
      console.error('Error in optimized intent detection:', error);
      return null;
    }
  };

  const handleIntentConfirmation = async (feedbackData) => {
    setIntentConfirmation(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await fetch('/api/optimized-intent/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          original_message: feedbackData.originalMessage,
          intent_match: feedbackData.intentMatch,
          user_confirmed: feedbackData.confirmed,
          session_id: sessionId,
          feedback: feedbackData.feedback
        })
      });

      if (!response.ok) {
        throw new Error('Confirmation feedback failed');
      }

      const result = await response.json();
      console.log('🚀 Intent confirmation processed:', result);
      
      // Close confirmation dialog
      setIntentConfirmation({
        visible: false,
        intentMatch: null,
        originalMessage: '',
        loading: false
      });

      // Execute the confirmed intent or proceed with the result
      if (feedbackData.confirmed) {
        // User confirmed - execute the intent directly
        await executeChatMessage(feedbackData.originalMessage, result.data);
      } else {
        // User denied - the backend will have used vector fallback
        await executeChatMessage(feedbackData.originalMessage, result.data);
      }

    } catch (error) {
      console.error('Error handling intent confirmation:', error);
      setError('Failed to process intent confirmation. Please try again.');
      setIntentConfirmation(prev => ({ ...prev, loading: false }));
    }
  };

  const handleIntentConfirmationCancel = () => {
    setIntentConfirmation({
      visible: false,
      intentMatch: null,
      originalMessage: '',
      loading: false
    });
  };

  // 🧠 COMPLIANCE-AWARE CHAT EXECUTION
  const executeComplianceChat = async (message, intentData, aiMessageId) => {
    try {
      // Build compliance context for API request
      const requestContext = {
        ...complianceContext,
        intent: intentData,
        user_context: {
          session_id: sessionId,
          current_page: window.location.pathname,
          timestamp: new Date().toISOString()
        }
      };

      // Call compliance-aware ALORA API
      const response = await fetch('/api/alora/compliance/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: message,
          user_context: requestContext,
          session_id: sessionId,
          include_analytics: true,
          max_relationship_depth: 2
        })
      });

      if (!response.ok) {
        throw new Error(`Compliance API error: ${response.status}`);
      }

      const result = await response.json();
      
      // Update the AI message with compliance response
      queryClient.setQueryData(['chatHistory', sessionId], (old) => {
        if (!old?.messages) return old;
        
        const updatedMessages = old.messages.map(m => {
          if (m.id === aiMessageId) {
            return {
              ...m,
              content: result.message,
              data: {
                ...result.data,
                intent: result.intent,
                skill_used: result.skill_used,
                sources: result.sources,
                recommendations: result.recommendations,
                next_actions: result.next_actions,
                context_used: result.context_used,
                compliance_mode: true
              },
              isThinking: false,
              compliance_response: true
            };
          }
          return m;
        });

        return {
          ...old,
          messages: updatedMessages
        };
      });

      console.log('✅ Compliance ALORA response processed:', result);
      
    } catch (error) {
      console.error('❌ Error in compliance chat:', error);
      
      // Update with error message
      queryClient.setQueryData(['chatHistory', sessionId], (old) => {
        if (!old?.messages) return old;
        return {
          ...old,
          messages: old.messages.map(m =>
            m.id === aiMessageId ? { 
              ...m, 
              content: 'I\'m having trouble accessing compliance intelligence right now. Let me help you with general GRC guidance instead.',
              error: true, 
              isThinking: false 
            } : m
          )
        };
      });
    }
  };

  const executeChatMessage = async (message, intentData = null) => {
    // Execute the actual chat message with the intent context
    if (!sessionId || !chatServiceRef.current) return;
    
    console.log('[Chat] Executing with intent:', intentData);
    
    // Add placeholder AI message immediately
    const aiMessageId = `ai_pending_${Date.now()}`;
    addMessage({
      id: aiMessageId,
      sender: 'ai',
      content: '',
      timestamp: new Date().toISOString(),
      isThinking: true
    });

    try {
      // 🧠 COMPLIANCE-AWARE ROUTING: Use compliance API when in compliance mode
      if (isComplianceMode) {
        // Use compliance-aware ALORA API
        console.log('[Chat] 🎯 Routing to compliance-aware ALORA AI');
        await executeComplianceChat(message, intentData, aiMessageId);
      } else {
        // Pass intent data as context to the standard chat service
        const context = intentData ? { intent: intentData } : {};
        
        await chatServiceRef.current.sendMessage(
        message,
        context,
        (chunk) => {
          // onStream: update the assistant message as it streams
          queryClient.setQueryData(['chatHistory', sessionId], (old) => {
            if (!old?.messages) return old;
            
            const updatedMessages = old.messages.map(m => {
              if (m.id === aiMessageId) {
                // Handle completion for Enhanced ALORA
                if (chunk.type === 'complete') {
                  return { 
                    ...m, 
                    data: { ...(m.data || {}), ...chunk.data },
                    isThinking: false,
                    enhanced_mode: chunk.enhanced_mode
                  };
                }
                
                const newContent = (m.content || '') + (chunk.content || '');
                const newData = chunk.data ? { ...(m.data || {}), ...chunk.data } : m.data;
                return { ...m, content: newContent, data: newData, isThinking: chunk.type !== 'complete' };
              }
              return m;
            });

            return {
              ...old,
              messages: updatedMessages
            };
          });
        },
        (clarificationPrompt) => {
          setClarification(clarificationPrompt);
        },
        (error) => {
          console.error('[Chat] AI error:', error);
          queryClient.setQueryData(['chatHistory', sessionId], (old) => {
            if (!old?.messages) return old;
            return {
              ...old,
              messages: old.messages.map(m =>
                m.id === aiMessageId ? { ...m, content: 'Something went wrong. Please try again.', error: true, isThinking: false } : m
              )
            };
          });
        },
        (thinkingStatus) => {
          // onThinking: update the thinking status for Enhanced ALORA
          setThinkingStatus(thinkingStatus);
          
          // Update the AI message with thinking status
          queryClient.setQueryData(['chatHistory', sessionId], (old) => {
            if (!old?.messages) return old;
            
            const updatedMessages = old.messages.map(m => {
              if (m.id === aiMessageId) {
                return { 
                  ...m, 
                  thinkingStatus: thinkingStatus.message,
                  thinkingStep: thinkingStatus.status
                };
              }
              return m;
            });

            return {
              ...old,
              messages: updatedMessages
            };
          });
        }
        );
      }
      
      // After stream is finished, set isThinking to false (for standard chat only)
      if (!isComplianceMode) {
        queryClient.setQueryData(['chatHistory', sessionId], (old) => {
          if (!old?.messages) return old;
          return {
            ...old,
            messages: old.messages.map(m =>
              m.id === aiMessageId ? { ...m, isThinking: false } : m
            )
          };
        });
      }

    } catch (err) {
      console.error('[Chat] Error in executeChatMessage:', err);
      queryClient.setQueryData(['chatHistory', sessionId], (old) => {
        if (!old?.messages) return old;
        return {
          ...old,
          messages: old.messages.map(m =>
            m.id === aiMessageId ? { ...m, content: 'Failed to get response.', error: true, isThinking: false } : m
          )
        };
      });
    }
  };

  // 🚀 OPTIMIZED sendMessage with keyword-first intent detection
  const sendMessage = useCallback(async (messageText) => {
    if (!sessionId || !chatServiceRef.current) return;
    
    // Add user message optimistically (ensure sender is 'user')
    const userMessage = {
      id: `msg_${Date.now()}`,
      sender: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    addMessage(userMessage);
    setIsTyping(true);
    console.log('[Chat] User message sent:', messageText);
    
    try {
      // Use the enhanced four-layer intent detection system
      const fallbackResult = await detectIntent(messageText, { sessionId });
      let intentResult = null;
      
      if (fallbackResult?.backendIntent) {
        intentResult = fallbackResult.backendIntent;
        console.log('🔍 Intent detection result:', intentResult);
        
        // Check if confirmation is required (medium confidence 50-80%)
        if (intentResult.requires_confirmation) {
          setIntentConfirmation({
            visible: true,
            intentMatch: intentResult,
            originalMessage: messageText,
            loading: false
          });
          setIsTyping(false);
          return; // Wait for user confirmation
        }
      }
      
      // Execute with the detected intent
      await executeChatMessage(messageText, intentResult);
      setIsTyping(false);
      
    } catch (err) {
      console.error('[Chat] Error in optimized sendMessage:', err);
      setError('Failed to send message. Please try again.');
      setIsTyping(false);
      
      // Add error message to chat
      addMessage({
        id: `error_${Date.now()}`,
        sender: 'ai',
        content: 'Sorry, I encountered an error processing your message. Please try again.',
        timestamp: new Date().toISOString(),
        error: true
      });
    }
  }, [sessionId, addMessage, queryClient]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory.messages, scrollToBottom]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() && !file) return;
    setSending(true);
    try {
      await sendMessage(messageInput);
      setMessageInput('');
      setFile(null);
    } catch (error) {}
    setSending(false);
  };

  // Provide all state and handlers in context
  const contextValue = {
    sessionId,
    messages: chatHistory.messages,
    isLoading: historyLoading,
    error,
    sendMessage,
    handleNewChat,
    setError,
  };

  // Render intent confirmation, clarification, and choices
  const renderIntentConfirmation = () => {
    if (confirmationStep > 0 && pendingIntent) {
      return (
        <div className="my-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
          <div className="font-semibold mb-2">Did I understand your intent correctly?</div>
          <div className="mb-2">Intent: <span className="font-mono">{pendingIntent}</span></div>
          <ChoiceButtons choices={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' }
          ]} onSelect={(val) => {/* handle intent confirm */}} />
        </div>
      );
    }
    return null;
  };

  const renderClarification = () => {
    if (clarification) {
      return (
        <div className="my-4 p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
          <div className="font-semibold mb-2">Clarification Needed</div>
          <div className="mb-2">{clarification.response}</div>
          <ChoiceButtons choices={clarification.options || []} onSelect={(val) => {/* handle clarification */}} />
        </div>
      );
    }
    return null;
  };

  // In ChatAssistant component, handle choice selection
  const handleChoice = useCallback((choice) => {
    sendMessage(choice);
  }, [sendMessage]);

  // Add a function to restart the chat
  const handleRestartChat = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  // Fetch inventory options for selects
  const fetchInventoryOptions = useCallback(async () => {
    try {
      const [teams, products, entities, risks] = await Promise.all([
        chatApi.getTeams(),
        chatApi.getProducts(),
        chatApi.getLicensedEntities(),
        chatApi.getRisks(),
      ]);
      setSelectOptions({
        teams: (teams || []).map(t => ({ value: t.name, label: t.name })),
        products: (products || []).map(p => ({ value: p.name, label: p.name })),
        entities: (entities || []).map(e => ({ value: e.name, label: e.name })),
        risks: (risks || []).map(r => ({ value: r.name, label: r.name })),
      });
    } catch {
      setSelectOptions({ teams: [], products: [], entities: [], risks: [] });
    }
  }, []);

  useEffect(() => {
    if (showDraftModal) fetchInventoryOptions();
  }, [showDraftModal, fetchInventoryOptions]);

  // Handler for when a draft is generated in chat
  const handleDraftIssue = (fields, explainability) => {
    setDraftIssue({ fields, explainability });
  };

  // Handler for opening/closing modal
  const handleViewDraft = () => setShowDraftModal(true);
  const handleCloseDraftModal = () => setShowDraftModal(false);

  // Handler for editing fields in modal
  const handleDraftFieldChange = (field, value) => {
    setDraftIssue(prev => ({
      ...prev,
      fields: { ...prev.fields, [field]: value }
    }));
  };

  // Handler for submitting draft as real issue
  const handleCreateIssue = async (e) => {
    e.preventDefault();
    setDraftModalLoading(true);
    try {
      // Only send filled fields
      const payload = Object.fromEntries(Object.entries(draftIssue.fields).filter(([_, v]) => v !== undefined && v !== null && v !== ''));
      // Ensure status is DRAFT unless explicitly set
      if (!payload.status) payload.status = 'DRAFT';
      const res = await issuesApi.createIssue(payload);
      setShowDraftModal(false);
      setDraftIssue(null);
      // Show 'issue created' bubble in chat
      addMessage({
        type: 'system',
        content: `✅ Issue created! [Click here to review issue](/issues/${res.id})`,
        isIssueCreated: true,
        issueId: res.id,
      });
      // Optionally, navigate to issue detail
      // navigate(`/issues/${res.id}`);
    } catch (err) {
      alert('Failed to create issue.');
    } finally {
      setDraftModalLoading(false);
    }
  };

  // In ChatAssistant, add a handler to open the draft modal from a chat message
  const handleOpenDraftFromMessage = (explainability) => {
    // Map explainability to fields and meta for the modal
    const fields = Object.fromEntries(Object.entries(explainability).map(([k, v]) => [k, v.value]));
    setDraftIssue({ fields, explainability });
    setShowDraftModal(true);
  };

  if (historyLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse flex space-x-4">
          <div className="h-8 w-8 rounded-full bg-gray-200"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={handleNewChat}
            className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600"
          >
            Start New Chat
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="h-full flex flex-col"
      style={{
        background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 25%, #f8fafc 50%, #f1f4f8 75%, #f9fafb 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 20s ease infinite'
      }}
    >
      <style>
        {`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>

      {/* Header */}
      <div 
        className="flex items-center justify-between p-6 border-b border-gray-200/40"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center shadow-lg overflow-hidden">
            <BrainIcon className="h-10 w-10" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {BRAND.copilot}
            </h1>
            <p className="text-sm text-gray-600 font-medium">
              {BRAND.product} Intelligence Copilot
            </p>
          </div>
        </div>
        
        {/* New Chat Button */}
        <button
          onClick={handleRestartChat}
          className="flex items-center gap-2 px-4 py-2 bg-white/80 hover:bg-white/90 backdrop-blur-sm border border-gray-200/60 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md"
        >
          <PlusIcon className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">New Chat</span>
        </button>
      </div>



      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Welcome Message */}
        {chatHistory.messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center shadow-lg mb-6 overflow-hidden">
              <BrainIcon className="h-16 w-16" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome to {BRAND.copilot}
            </h2>
            <p className="text-lg text-gray-700 mb-2">
              {BRAND.copilotAttribution}
            </p>
            <p className="text-gray-600 text-base mb-8 max-w-lg">
              Your intelligent {BRAND.product} copilot is here to help you manage risks, compliance, and governance with precision and insight.
            </p>
            
            {/* Suggested Prompts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl">
              {suggestedPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => setMessageInput(prompt.replace(/^[🔍📋⚠️📊🛡️📈]\s/, ''))}
                  className="p-4 bg-white/80 hover:bg-white/90 backdrop-blur-sm border border-gray-200/60 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md text-left group"
                >
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    {prompt}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <AnimatePresence>
          {chatHistory.messages.map((msg, idx) => (
            <ChatMessage 
              key={idx} 
              msg={msg} 
              onChoice={handleChoice}
            />
          ))}
        </AnimatePresence>
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div 
        className="p-6 border-t border-gray-200/40"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="flex items-end gap-3">
            {/* File Upload */}
            <button
              type="button"
              onClick={() => document.getElementById('file-input').click()}
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/80 hover:bg-white/90 backdrop-blur-sm border border-gray-200/60 flex items-center justify-center shadow-sm transition-all duration-200 hover:shadow-md"
              disabled={sending}
            >
              <PhotoIcon className="h-5 w-5 text-gray-600" />
            </button>
            <input
              type="file"
              id="file-input"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0])}
              accept=".txt,.pdf,.doc,.docx,.csv,.json"
            />

            {/* Message Input */}
            <div className="flex-1 relative">
              <textarea
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder={
                  isComplianceMode
                    ? `Ask ${BRAND.copilotShort} about compliance requirements, regulations, or risk forecasting...`
                    : `Ask ${BRAND.copilot} anything about your GRC needs...`
                }
                className="w-full px-4 py-3 bg-white/80 backdrop-blur-sm border border-gray-200/60 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400/30 focus:border-gray-400 resize-none text-gray-800 placeholder-gray-500"
                rows={1}
                style={{
                  minHeight: '48px',
                  maxHeight: '120px',
                  resize: 'none'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                disabled={sending}
              />
            </div>

            {/* Send Button */}
            <button
              type="submit"
              disabled={sending || !messageInput.trim()}
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <PaperAirplaneIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          {/* File Preview */}
          {file && (
            <div className="mt-3 p-3 bg-gray-50/80 backdrop-blur-sm border border-gray-200/60 rounded-xl">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800">
                  📎 {file.name}
                </span>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-gray-600 hover:text-gray-800 text-sm"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Modals and Dialogs */}
      <DraftIssueModal
        open={showDraftModal}
        onClose={handleCloseDraftModal}
        draftFields={draftIssue?.fields || {}}
        fieldMeta={draftIssue?.explainability || {}}
        selectOptions={selectOptions}
        onChange={handleDraftFieldChange}
        onSubmit={handleCreateIssue}
        loading={draftModalLoading}
      />
      
      {/* 🚀 OPTIMIZED INTENT CONFIRMATION DIALOG */}
      <IntentConfirmationDialog
        visible={intentConfirmation.visible}
        intentMatch={intentConfirmation.intentMatch}
        originalMessage={intentConfirmation.originalMessage}
        onConfirm={handleIntentConfirmation}
        onDeny={handleIntentConfirmation}
        onCancel={handleIntentConfirmationCancel}
        loading={intentConfirmation.loading}
      />
    </div>
  );
};

export default ChatAssistant; 